import { TEMPLATES_BY_TIER } from '../combat/enemies';
import type { Biome, Tile, TileLifecycleState, TileMetadata, TileType } from '../core/tile';
import { CHUNK_SIZE, type Chunk, type ChunkCoord } from '../core/world';
import { encounterRateAtDistance, safeZoneDistance } from '../lifecycle/frontierRisk';
import { pickTier, tierBand, tierWeightsAt } from '../lifecycle/tierBands';
import type { WorldGen } from './WorldGen';
import { biomeAt } from './biome';
import { forcedBorderCells } from './border-handshake';
import { localIdx, unifyConnectivity } from './connectivity';
import { type SettlementAnchor, settlementAnchorInChunk } from './frontierSettlement';
import { hashChunk, mulberry32 } from './noise';

/**
 * Default world generator (ADR-0006 + ADR-0013). 8×8 chunk shape, biome
 * layer (Voronoi cells + Markov transitions) seeds impassability before
 * the existing value-fill + CA smoothing, canonical-edge-hash border
 * handshake, post-process corridor carving for interior connectivity,
 * and the origin-chunk invariant (ground at (0, 0), pillar at (0, 1),
 * passable between them — plus the HEX-28 spawn-cluster biome override).
 *
 * One endless world (ADR-0018 §1): distance from origin is the only
 * progression axis. There are no realms and no portals.
 */
export class ChunkedWorldGen implements WorldGen {
  generateChunk(coord: ChunkCoord, seed: number): Chunk {
    const prng = mulberry32(hashChunk(seed, coord.cq, coord.cr));

    // 1. Biome layer (ADR-0013 §6 step 1).
    const originalBiomes = new Array<Biome>(CHUNK_SIZE * CHUNK_SIZE);
    for (let lr = 0; lr < CHUNK_SIZE; lr++) {
      for (let lq = 0; lq < CHUNK_SIZE; lq++) {
        const q = coord.cq * CHUNK_SIZE + lq;
        const r = coord.cr * CHUNK_SIZE + lr;
        originalBiomes[localIdx(lq, lr)] = biomeAt(seed, q, r);
      }
    }
    const biomeImpassable = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
    for (let i = 0; i < biomeImpassable.length; i++) {
      const b = originalBiomes[i] as Biome;
      biomeImpassable[i] = b === 'lake' || b === 'mountain' ? 1 : 0;
    }

    // 2. Initial random fill — kept as-is so PRNG-stream alignment with
    // non-origin chunks is preserved (every cell consumes one prng()).
    const passable = initialFill(prng);
    // 3. Overlay biome impassability after the random fill (ADR-0013 §6 step 3:
    // "operates only on biome-passable cells").
    for (let i = 0; i < passable.length; i++) {
      if (biomeImpassable[i] === 1) passable[i] = 0;
    }
    // 4. CA smoothing — biome-impassable cells stay pinned at 0.
    smooth(passable, biomeImpassable);

    // 5. Forced border cells (ADR-0006 §5 handshake; ADR-0013 §5 downgrade).
    const forced = forcedBorderCells(coord, seed);
    for (const [lq, lr] of forced) passable[localIdx(lq, lr)] = 1;

    const isOrigin = coord.cq === 0 && coord.cr === 0;
    if (isOrigin) {
      // Origin spawn-town cluster (ADR-0006 HEX-28 amendment): all five
      // cluster cells must be passable and mutually connected.
      for (const [lq, lr] of SPAWN_CLUSTER_CELLS) {
        passable[localIdx(lq, lr)] = 1;
      }
    }

    // Frontier-settlement cluster (HEX-93). Deterministic from (coord, seed) via
    // an INDEPENDENT hash — never the chunk PRNG stream — so non-settlement
    // tiles stay byte-identical. The origin keep-out (≥ 60 hexes) means this
    // never fires on the origin chunk, so it composes cleanly with the
    // spawn-cluster invariant above.
    const settlement = settlementAnchorInChunk(coord, seed);
    const settlementCells = settlement === null ? null : settlementClusterCells(settlement);
    if (settlementCells !== null) {
      for (const [lq, lr] of Object.values(settlementCells)) {
        passable[localIdx(lq, lr)] = 1;
      }
    }

    const carveForced: ReadonlyArray<readonly [number, number]> = isOrigin
      ? [...forced, ...SPAWN_CLUSTER_CELLS]
      : settlementCells !== null
        ? [...forced, ...Object.values(settlementCells)]
        : forced;
    unifyConnectivity(passable, carveForced);

    const tiles: Tile[] = new Array(CHUNK_SIZE * CHUNK_SIZE);
    // Row-major in r then q.
    for (let lr = 0; lr < CHUNK_SIZE; lr++) {
      for (let lq = 0; lq < CHUNK_SIZE; lq++) {
        const q = coord.cq * CHUNK_SIZE + lq;
        const r = coord.cr * CHUNK_SIZE + lr;
        const idx = lr * CHUNK_SIZE + lq;
        const isPassable = passable[idx] === 1;
        const effective = effectiveBiome(
          originalBiomes[idx] as Biome,
          isPassable,
          isOrigin,
          lq,
          lr,
        );
        tiles[idx] = buildTile(
          q,
          r,
          lq,
          lr,
          isPassable,
          prng,
          isOrigin,
          seed,
          effective,
          settlementCells,
        );
      }
    }

    return { coord, seed, tiles };
  }
}

// Tuned to satisfy the ADR-0006 topology metric (≥5% of passable hexes have
// ≥3 impassable neighbours) while leaving plenty of room for the corridor-
// carving step to keep interior connectivity tight. Starts passable-leaning
// so the world is navigable, not a single-file maze; the impassable threshold
// still biases CA toward keeping ridges and narrow tunnels between them.
// ADR-0006 HEX-135 amendment (candidate A): p 0.42 → 0.60 to thin the maze.
const INITIAL_PASSABLE_P = 0.6;
const CA_ITERATIONS = 4;
const CA_IMPASSABLE_THRESHOLD = 3;
const CA_PASSABLE_THRESHOLD = 1;

const LOCAL_NEIGHBOUR_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

function initialFill(prng: () => number): Uint8Array {
  const grid = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
  // Iterate lq outer / lr inner so the PRNG-call order stays stable.
  for (let lq = 0; lq < CHUNK_SIZE; lq++) {
    for (let lr = 0; lr < CHUNK_SIZE; lr++) {
      grid[localIdx(lq, lr)] = prng() < INITIAL_PASSABLE_P ? 1 : 0;
    }
  }
  return grid;
}

function smooth(grid: Uint8Array, biomeImpassable: Uint8Array): void {
  const next = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
  for (let iter = 0; iter < CA_ITERATIONS; iter++) {
    next.set(grid);
    for (let lq = 0; lq < CHUNK_SIZE; lq++) {
      for (let lr = 0; lr < CHUNK_SIZE; lr++) {
        const idx = localIdx(lq, lr);
        if (biomeImpassable[idx] === 1) {
          // Biome lake/mountain cells stay impassable through smoothing
          // (ADR-0013 §6 — CA operates only on biome-passable cells).
          next[idx] = 0;
          continue;
        }
        let impassable = 0;
        for (const [dq, dr] of LOCAL_NEIGHBOUR_OFFSETS) {
          const nq = lq + dq;
          const nr = lr + dr;
          if (nq < 0 || nq >= CHUNK_SIZE || nr < 0 || nr >= CHUNK_SIZE) continue;
          if (grid[localIdx(nq, nr)] === 0) impassable++;
        }
        if (impassable >= CA_IMPASSABLE_THRESHOLD) next[idx] = 0;
        else if (impassable <= CA_PASSABLE_THRESHOLD) next[idx] = 1;
      }
    }
    grid.set(next);
  }
}

function isBorder(lq: number, lr: number): boolean {
  return lq === 0 || lq === CHUNK_SIZE - 1 || lr === 0 || lr === CHUNK_SIZE - 1;
}

/**
 * Chest and merchant rates by tier band (ADR-0018 §2 density banding, HEX-118).
 * Bands match `TIER_BAND_MAX_DISTANCES` breakpoints [30, 80, 160, 256] — 5 bands.
 *
 *   Band 0  (d < 30)   — sparse POIs near origin; world starts legible
 *   Band 1  (30–80)    — light density; the frontier begins to reward exploration
 *   Band 2  (80–160)   — moderate density; mid-frontier richness
 *   Band 3  (160–256)  — high density; deep frontier is meaningfully richer
 *   Band 4  (d ≥ 256)  — peak density; furthest reaches are the most rewarding
 */
export const CHEST_BAND_RATES: readonly number[] = [0.01, 0.02, 0.04, 0.06, 0.08];
export const MERCHANT_BAND_RATES: readonly number[] = [0.005, 0.01, 0.02, 0.03, 0.04];
const DANGEROUS_RATE = 0.04;

function chestRateAtDistance(d: number): number {
  return CHEST_BAND_RATES[tierBand(d)] ?? CHEST_BAND_RATES[CHEST_BAND_RATES.length - 1] ?? 0;
}

function merchantRateAtDistance(d: number): number {
  return (
    MERCHANT_BAND_RATES[tierBand(d)] ?? MERCHANT_BAND_RATES[MERCHANT_BAND_RATES.length - 1] ?? 0
  );
}

/**
 * Per-biome multipliers for the four discoverable categories (ADR-0013
 * §4). Lake / mountain are 0 across the board because rolls don't fire
 * on impassable hexes — kept here so the multiplier table is exhaustive
 * over the Biome union.
 *
 * Merchant column scoping (resolved per the HEX-30 brainstorm note):
 * this generator only rolls `traveller-merchant` — `shop` is town-bound
 * and seeded by the spawn-cluster, not biome-rolled. So the `merchant`
 * column applies to traveller-merchant exclusively. Formal ADR-0013
 * amendment to tighten the column heading is optional; the code carries
 * the constraint either way.
 */
const BIOME_AFFINITY: Readonly<
  Record<
    Biome,
    {
      readonly encounter: number;
      readonly chest: number;
      readonly merchant: number;
      readonly dangerous: number;
    }
  >
> = {
  plain: { encounter: 1.0, chest: 1.0, merchant: 1.0, dangerous: 1.0 },
  forest: { encounter: 1.0, chest: 1.5, merchant: 1.5, dangerous: 0.5 },
  snow: { encounter: 1.4, chest: 0.8, merchant: 0.7, dangerous: 1.0 },
  lake: { encounter: 0, chest: 0, merchant: 0, dangerous: 0 },
  mountain: { encounter: 0, chest: 0, merchant: 0, dangerous: 0 },
};

/**
 * Origin spawn-town cluster cells (ADR-0006 §HEX-28 amendment): pillar +
 * shop + town + starter road around the spawn. Per ADR-0013 §8 and the
 * HEX-28 extension, these cells always *report* biome `plain` when the
 * underlying biome layer resolves to lake/mountain — anti-pathological
 * "no spawn-on-water" guarantee without per-seed special casing. The
 * cluster tile *types* (shop/town/road) themselves are placed by a
 * separate follow-up ticket; HEX-30 only handles the biome override.
 */
const SPAWN_CLUSTER_CELLS: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [0, 1],
  [1, 0],
  [1, 1],
  [0, 2],
];

/**
 * Frontier-settlement cluster footprint (HEX-93), in LOCAL chunk coords
 * relative to the settlement anchor cell. The anchor (town) sits at local
 * (3, 3) — see `frontierSettlement.ANCHOR_LOCAL` — and the cluster spreads into
 * adjacent interior cells so it never touches a chunk edge (cluster cells reuse
 * the discoverable interior-only discipline):
 *
 *   town   — the anchor; heal + (future) interaction centre (TownFlow reuse)
 *   shop   — adjacent; per-tile-keyed stock (ShopFlow reuse, HEX-72 keying)
 *   road   — starter segment, road network derived from the anchor (ADR-0012 §6)
 *   pillar slot — RESERVED as `ground` for HEX-92 (this ticket places no pillar;
 *                 ADR-0012 §9 wants a town to have a pillar neighbour — the slot
 *                 is that neighbour, filled when HEX-92's pillar roll lands)
 *
 * Offsets are added to the anchor's LOCAL (lq, lr). Anchor local is (3, 3), so
 * every cell lands in the interior 1..6 range of an 8-wide chunk.
 */
const SETTLEMENT_CELL_OFFSETS = {
  town: [0, 0],
  shop: [1, 0],
  road: [0, 1],
  pillarSlot: [0, -1],
} as const;

/** Resolved local (lq, lr) per cluster role for a settlement in this chunk. */
interface SettlementClusterCells {
  readonly town: readonly [number, number];
  readonly shop: readonly [number, number];
  readonly road: readonly [number, number];
  readonly pillarSlot: readonly [number, number];
}

/**
 * Resolve the frontier-settlement cluster's LOCAL cells from its world-coord
 * anchor. The anchor's local coords are its world coords mod CHUNK_SIZE (the
 * anchor always lies inside its own chunk, never on an edge — see
 * `frontierSettlement.ANCHOR_LOCAL`), so the offsets land in the interior.
 */
function settlementClusterCells(anchor: SettlementAnchor): SettlementClusterCells {
  const alq = ((anchor.q % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
  const alr = ((anchor.r % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
  const cell = (off: readonly [number, number]): readonly [number, number] => [
    alq + off[0],
    alr + off[1],
  ];
  return {
    town: cell(SETTLEMENT_CELL_OFFSETS.town),
    shop: cell(SETTLEMENT_CELL_OFFSETS.shop),
    road: cell(SETTLEMENT_CELL_OFFSETS.road),
    pillarSlot: cell(SETTLEMENT_CELL_OFFSETS.pillarSlot),
  };
}

function isSpawnClusterCell(lq: number, lr: number): boolean {
  for (const [cq, cr] of SPAWN_CLUSTER_CELLS) {
    if (cq === lq && cr === lr) return true;
  }
  return false;
}

/** Match a (lq, lr) against a cluster offset cell. */
function isCell(lq: number, lr: number, cell: readonly [number, number]): boolean {
  return lq === cell[0] && lr === cell[1];
}

/**
 * Deterministic road-network id for a frontier settlement's starter road
 * (HEX-93). Origin's starter road is network 0; frontier roads take a non-zero
 * id derived from the anchor world coords so distinct settlements never share a
 * network (which would later let road-formation wrongly bridge them). Forced
 * non-zero so it can never collide with origin's network 0.
 */
function frontierRoadNetworkId(anchorQ: number, anchorR: number): number {
  let h = 0x811c9dc5;
  for (const x of [anchorQ, anchorR]) {
    h ^= x | 0;
    h = Math.imul(h, 0x01000193);
  }
  return (Math.abs(h) % 0x7fffffff) + 1;
}

/**
 * Place a frontier-town cluster cell (HEX-93 + HEX-115), or null when `(lq, lr)`
 * is not a cluster cell (caller falls through to the normal roll). Every branch
 * consumes exactly ONE `prng()` so the chunk stream stays aligned — the
 * determinism guarantee.
 *
 * Cluster (anchor at the town cell):
 *   - town       — boss-held objective; anchorTiles points at the reserved pillar
 *                  slot so Wave-3 can activate it on liberation.
 *   - shop       — ShopFlow reuse; per-shop stock keyed by `(q, r)`.
 *   - road       — starter segment with a settlement-unique road-network id.
 *   - pillarSlot — RESERVED as `ground`; materialises as `pillar` on liberation
 *                  (Wave-3, ADR-0018 §1). The town's anchorTiles.pillar already
 *                  points here.
 */
function buildSettlementCell(
  q: number,
  r: number,
  lq: number,
  lr: number,
  prng: () => number,
  seed: number,
  biome: Biome,
  cells: SettlementClusterCells,
): Tile | null {
  if (isCell(lq, lr, cells.town)) {
    prng();
    const [sdq, sdr] = SETTLEMENT_CELL_OFFSETS.shop;
    const [pdq, pdr] = SETTLEMENT_CELL_OFFSETS.pillarSlot;
    return makeTile(q, r, 'town', biome, seed, {
      // pillarHex → reserved ground slot, activates to pillar on liberation (Wave-3).
      pillarHex: { q: q + pdq, r: r + pdr },
      shopHex: { q: q + sdq, r: r + sdr },
    });
  }
  if (isCell(lq, lr, cells.shop)) {
    prng();
    return makeTile(q, r, 'shop', biome, seed);
  }
  if (isCell(lq, lr, cells.road)) {
    prng();
    // Anchor (town) world coords = this road's coords minus the road offset.
    const [rdq, rdr] = SETTLEMENT_CELL_OFFSETS.road;
    return makeTile(q, r, 'road', biome, seed, {
      roadNetworkId: frontierRoadNetworkId(q - rdq, r - rdr),
    });
  }
  if (isCell(lq, lr, cells.pillarSlot)) {
    // Reserved slot — stays ground until the town is liberated (Wave-3).
    // Consumes one prng() for stream alignment.
    prng();
    return makeTile(q, r, 'ground', biome, seed);
  }
  return null;
}

/**
 * Resolve the on-tile biome from the underlying Voronoi/Markov result
 * plus the two override rules:
 *
 * 1. Forced-passable downgrade (ADR-0013 §5): when `unifyConnectivity`
 *    or the border handshake carves through a lake/mountain cell, the
 *    biome becomes `plain` (acts as ford / pass).
 * 2. Origin-cluster anti-pathological (ADR-0013 §8 + HEX-28): for the
 *    origin chunk `(0, 0)`, the 5 cluster cells override to `plain`
 *    when the underlying biome is lake/mountain. Passable cluster
 *    biomes (plain/forest/snow) pass through as themselves so a future
 *    renderer tinting layer can pick up the underlying region.
 */
function effectiveBiome(
  underlying: Biome,
  isPassable: boolean,
  isOrigin: boolean,
  lq: number,
  lr: number,
): Biome {
  const isCluster = isOrigin && isSpawnClusterCell(lq, lr);
  if ((underlying === 'lake' || underlying === 'mountain') && (isPassable || isCluster)) {
    return 'plain';
  }
  return underlying;
}

function buildTile(
  q: number,
  r: number,
  lq: number,
  lr: number,
  passable: boolean,
  prng: () => number,
  isOrigin: boolean,
  seed: number,
  biome: Biome,
  settlementCells: SettlementClusterCells | null,
): Tile {
  if (settlementCells !== null) {
    // Frontier settlement cluster (HEX-93). Each cluster cell consumes exactly
    // ONE prng() — the same draw count as the normal-roll branch below — so the
    // chunk's stream stays aligned and every NON-cluster tile is byte-identical
    // to a settlement-free generation (the determinism proof the ticket wants).
    const settlementTile = buildSettlementCell(q, r, lq, lr, prng, seed, biome, settlementCells);
    if (settlementTile !== null) return settlementTile;
  }
  if (isOrigin && lq === 0 && lr === 0) {
    // Always consume the PRNG slot so non-origin chunks stream the same length.
    prng();
    return makeTile(q, r, 'ground', biome, seed);
  }
  if (isOrigin && lq === 0 && lr === 1) {
    prng();
    return makeTile(q, r, 'pillar', biome, seed);
  }
  if (isOrigin && lq === 1 && lr === 0) {
    prng();
    return makeTile(q, r, 'shop', biome, seed);
  }
  if (isOrigin && lq === 1 && lr === 1) {
    // Origin chunk local coords are world coords (cq=cr=0).
    prng();
    return makeTile(q, r, 'town', biome, seed, {
      pillarHex: { q: 0, r: 1 },
      shopHex: { q: 1, r: 0 },
    });
  }
  if (isOrigin && lq === 0 && lr === 2) {
    prng();
    return makeTile(q, r, 'road', biome, seed, { roadNetworkId: 0 });
  }

  const roll = prng();

  if (!passable) {
    return makeTile(q, r, 'mountain', biome, seed);
  }

  const aff = BIOME_AFFINITY[biome];
  // HEX-42 frontier-risk: encounter rate is banded by distance from origin
  // (ADR-0018 §4 — town proximity does not ease until Wave-3 liberation).
  // Chest and merchant rates are distance-banded (ADR-0018 §2 — HEX-118).
  const d = safeZoneDistance(q, r, seed);
  const encounterRate = encounterRateAtDistance(d) * aff.encounter;
  const chestRate = chestRateAtDistance(d) * aff.chest;
  const merchantRate = merchantRateAtDistance(d) * aff.merchant;

  // Discoverables only in passable interior cells (ADR-0006).
  if (!isBorder(lq, lr)) {
    if (roll < encounterRate) return makeTile(q, r, 'encounter', biome, seed);
    if (roll < encounterRate + chestRate) return makeTile(q, r, 'chest', biome, seed);
    if (roll < encounterRate + chestRate + merchantRate) {
      return makeTile(q, r, 'traveller-merchant', biome, seed);
    }
  }

  // `dangerous` is terrain (visible-face), not a discoverable, so it can
  // appear on a chunk edge. Reserve a slice of the roll range above the
  // discoverable bands (in interior) or starting from zero (on borders).
  const danBase = isBorder(lq, lr) ? 0 : encounterRate + chestRate + merchantRate;
  const dangerousRate = DANGEROUS_RATE * aff.dangerous;
  if (roll < danBase + dangerousRate) {
    return makeTile(q, r, 'dangerous', biome, seed);
  }
  return makeTile(q, r, 'ground', biome, seed);
}

/**
 * Optional cluster-cell overrides applied at materialisation. The origin
 * spawn-town cluster needs the `town` to point at the (pillar, shop) it sits
 * adjacent to, and the starter `road` to belong to network 0 — both of which
 * are properties of the placement, not derivable from `(type, q, r, seed)`.
 */
interface MakeTileOverrides {
  readonly pillarHex?: { q: number; r: number };
  readonly shopHex?: { q: number; r: number };
  readonly roadNetworkId?: number;
}

function makeTile(
  q: number,
  r: number,
  type: TileType,
  biome: Biome,
  seed: number,
  overrides: MakeTileOverrides = {},
): Tile {
  const metadata: TileMetadata = {
    discoveredBy: null,
    discoveredAt: null,
    uncoveredBy: null,
    uncoveredAt: null,
    lastTraveller: null,
    lastTravelledAt: null,
    fallenCount: 0,
    fallenCharacters: [],
  };
  return {
    q,
    r,
    type,
    biome,
    state: 'unrevealed',
    metadata,
    crossings: 0,
    lifecycleState: initialLifecycleState(type, q, r, seed, overrides),
    roadNetworkId: overrides.roadNetworkId ?? null,
  };
}

/**
 * Per-type lifecycle blob at generation. Encounter tiles get a deterministic
 * `enemyId` derived from `(q, r, seed)` so the persistent identity
 * survives respawn cycles without storing the picker seed (ADR-0012 §2).
 * Other types get their starting state per ADR-0012 §3 / §5.
 *
 * `town`, `shop`, and `road` types are not generated by this generator yet —
 * they come online via the lifecycle conversion (road) and adjacency-gated
 * spawn-town generation (shop / town, HEX-28); their cases here are
 * defensive so the union is exhaustively handled.
 */
function initialLifecycleState(
  type: TileType,
  q: number,
  r: number,
  seed: number,
  overrides: MakeTileOverrides,
): TileLifecycleState | null {
  switch (type) {
    case 'encounter':
      return {
        kind: 'encounter',
        enemyId: pickEnemyId(q, r, seed),
        slayerBuffStack: 0,
        resolvedAt: null,
      };
    case 'chest':
      return { kind: 'chest', looterCount: 0, degraded: false };
    case 'ground':
      return { kind: 'ground', latentRollFired: false };
    case 'road':
      return { kind: 'road' };
    case 'shop':
      return { kind: 'shop', inventoryRefreshAt: null };
    case 'town':
      // Origin spawn-town cluster passes the (pillar, shop) world-coords it
      // sits adjacent to (HEX-28 amendment §"Interaction with HEX-29"); other
      // call-sites fall back to self-coords until the adjacency-gated town
      // generator lands.
      return {
        kind: 'town',
        anchorTiles: {
          pillar: overrides.pillarHex ?? { q, r },
          shop: overrides.shopHex ?? { q, r },
        },
      };
    case 'traveller-merchant':
      return { kind: 'travellerMerchant', interacted: false };
    case 'mountain':
    case 'pillar':
    case 'dangerous':
      return null;
  }
}

/**
 * Pick a pre-placed encounter's enemy id, tier-scaled by distance from origin
 * (HEX-64; was a flat uniform hash over the whole roster). Fully deterministic:
 * the same `(q, r, seed)` always yields the same enemy.
 *
 *   1. Tier rolls from `tierWeightsAt(d)` via a unit value derived from
 *      `mix(q, r, seed)`, so the spawn region is pure-common and high tiers
 *      unlock only on the deep frontier (shared band table with the latent
 *      picker; see `lifecycle/tierBands.ts`).
 *   2. Within-tier, a second independent hash `mix(q, r, seed, SALT)` indexes
 *      the tier roster uniformly.
 *
 * Empty-tier fallback (walk down to the nearest non-empty tier) lives in the
 * shared `pickTier` helper.
 */
export function pickEnemyId(q: number, r: number, seed: number): string {
  // Tier eases near a frontier settlement too (HEX-93): the safe-zone metric
  // folds in nearby frontier towns via `seed`, so an encounter beside a town is
  // tamer than one in open frontier at the same origin distance.
  const weights = tierWeightsAt(safeZoneDistance(q, r, seed));
  const tierRoll = unitFromHash(mix(q, r, seed));
  const tier = pickTier(tierRoll, weights, (t) => TEMPLATES_BY_TIER[t].length > 0);
  const pool = TEMPLATES_BY_TIER[tier];
  if (pool.length === 0) throw new Error('no enemies registered');
  const within = Math.abs(mix(q, r, seed, WITHIN_TIER_SALT)) % pool.length;
  const template = pool[within];
  if (template === undefined) throw new Error(`enemy lookup miss at tier ${tier} idx ${within}`);
  return template.id;
}

/** Salt for the within-tier hash so it decorrelates from the tier roll. */
const WITHIN_TIER_SALT = 0x9e3779b9 | 0;

/** Map a signed 32-bit hash into `[0, 1)` for the tier roll. Never returns 1. */
function unitFromHash(h: number): number {
  return (Math.abs(h) % 0x7fffffff) / 0x7fffffff;
}

// FNV-ish small integer hash, mirrors `encounter.ts` so generation and the
// (now-vestigial) hash-pick stay byte-aligned during the transition.
function mix(...inputs: number[]): number {
  let h = 0x811c9dc5;
  for (const x of inputs) {
    h ^= x | 0;
    h = Math.imul(h, 0x01000193);
  }
  return h | 0;
}
